import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { hasRole, type PermissionKey, type Role } from '@nexa/auth';
import { useSession } from './use-session';
import { useCachedAccess } from '@/lib/access/use-access';
import { permissionLabel } from '@/lib/access/labels';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// <RoleGate min={Role.OPS_SUPERVISOR}>...</RoleGate> per docs/ui/operator-ui-tds.md §9.
// If the session role doesn't satisfy `min` via hasRole():
//   - For a single interactive element (button-shaped), clone with `disabled` + wrap in tooltip
//     explaining why it's disabled.
//   - For non-interactive content, render nothing.
//
// users-roles-permissions R5 — back-compat bridge until R10 migrates the call sites to
// `useAccess()` / `<Can>`:
//   - the role is `useSession().role` (now the BFF-derived legacy role, M1), read so that a
//     session with NO role — a member with no access assigned, a role-less token — renders
//     nothing instead of crashing;
//   - an optional `permission` gates on the permission instead of the role, so a call
//     site can migrate without changing its disabled-with-tooltip behaviour.

/**
 * `useSession().role`, or `null` when the session has none. `useSession` runs all of its
 * hooks before it can throw (see its INVARIANT), so catching here keeps the hook order
 * stable across renders.
 */
function useRoleOrNull(): Role | null {
  try {
    return useSession().role;
  } catch {
    return null;
  }
}

type RoleGateProps = {
  children: ReactNode;
  /** Override the disabled tooltip copy. */
  tooltip?: string;
  /** Render the gate as hidden (no tooltip / no disabled element) when denied. */
  hideWhenDenied?: boolean;
} & ({ min: Role; permission?: PermissionKey } | { min?: Role; permission: PermissionKey });

export function RoleGate({
  min,
  permission,
  children,
  tooltip,
  hideWhenDenied = false,
}: RoleGateProps) {
  const { t } = useTranslation('admin');
  const role = useRoleOrNull();
  const access = useCachedAccess();
  const allowed =
    permission !== undefined
      ? access.can(permission)
      : role !== null && min !== undefined && hasRole(role, min);

  if (allowed) {
    return <>{children}</>;
  }

  // No role at all (role-gated, no permission given): nothing to explain — hide.
  if (hideWhenDenied || (permission === undefined && role === null)) {
    return null;
  }

  const message =
    tooltip ??
    (permission !== undefined
      ? t('roleGate.requiresPermission', { permission: permissionLabel(t, permission) })
      : t('roleGate.requires', { role: min }));

  if (isValidElement<{ disabled?: boolean; 'aria-disabled'?: boolean }>(children)) {
    const disabledChild = cloneElement(children, {
      disabled: true,
      'aria-disabled': true,
    }) as ReactElement;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block">{disabledChild}</span>
          </TooltipTrigger>
          <TooltipContent>{message}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}
